import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const BackupManagement = () => {
    const navigate = useNavigate();
    const [backups, setBackups] = useState([]);
    const [stats, setStats] = useState(null);
    const [schedulerStatus, setSchedulerStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [creatingBackup, setCreatingBackup] = useState(false);
    const [restoring, setRestoring] = useState(false);

    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchBackupData();
    }, []);

    const fetchBackupData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch backups list
            const backupsResponse = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/backup/list`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (backupsResponse.status === 403) {
                setError('Acesso negado. Apenas usuários master podem acessar backups.');
                setLoading(false);
                return;
            }

            if (!backupsResponse.ok) {
                throw new Error('Erro ao carregar backups');
            }

            const backupsData = await backupsResponse.json();
            setBackups(backupsData.backups || []);
            setStats(backupsData.stats || null);

            // Fetch scheduler status
            const statusResponse = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/backup/status`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                setSchedulerStatus(statusData.scheduler || null);
            }

        } catch (err) {
            setError(err.message || 'Erro ao carregar dados de backup');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBackup = async () => {
        if (!window.confirm('Deseja criar um backup manual agora?')) {
            return;
        }

        setCreatingBackup(true);
        setError(null);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/backup/create`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Erro ao criar backup');
            }

            const result = await response.json();
            
            if (result.success) {
                alert(`Backup criado com sucesso!\n\nNome: ${result.backup_name}\nTamanho: ${result.size_mb} MB`);
                fetchBackupData(); // Reload data
            } else {
                throw new Error(result.error || 'Falha ao criar backup');
            }

        } catch (err) {
            setError(err.message || 'Erro ao criar backup');
            alert(`Erro ao criar backup: ${err.message}`);
        } finally {
            setCreatingBackup(false);
        }
    };

    const handleRestoreBackup = async (backupName) => {
        if (!window.confirm(
            `⚠️ ATENÇÃO! ⚠️\n\n` +
            `Esta operação irá SOBRESCREVER todos os dados atuais do sistema!\n\n` +
            `Backup a restaurar: ${backupName}\n\n` +
            `Tem certeza que deseja continuar?`
        )) {
            return;
        }

        setRestoring(true);
        setError(null);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/backup/restore/${backupName}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Erro ao restaurar backup');
            }

            const result = await response.json();
            
            if (result.success) {
                alert(
                    `Backup restaurado com sucesso!\n\n` +
                    `${result.message}\n\n` +
                    `O sistema será recarregado em 3 segundos...`
                );
                
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            } else {
                throw new Error(result.error || 'Falha ao restaurar backup');
            }

        } catch (err) {
            setError(err.message || 'Erro ao restaurar backup');
            alert(`Erro ao restaurar backup: ${err.message}`);
        } finally {
            setRestoring(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center">
                <div className="text-white text-xl">Carregando backups...</div>
            </div>
        );
    }

    if (error && error.includes('Acesso negado')) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center">
                <div className="bg-white rounded-lg p-8 shadow-2xl max-w-md">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Acesso Negado</h2>
                    <p className="text-gray-700 mb-6">{error}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition"
                    >
                        Voltar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 py-8 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-2xl p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800 mb-2">
                                🛡️ Sistema de Backup
                            </h1>
                            <p className="text-gray-600">
                                Gerenciamento de backups e recuperação de dados
                            </p>
                        </div>
                        <button
                            onClick={() => navigate(-1)}
                            className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition"
                        >
                            Voltar
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-sm text-gray-600 mb-2">Total de Backups</div>
                            <div className="text-3xl font-bold text-purple-600">
                                {stats.total_backups}
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-sm text-gray-600 mb-2">Espaço Total</div>
                            <div className="text-3xl font-bold text-blue-600">
                                {stats.total_size_mb} MB
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-sm text-gray-600 mb-2">Retenção</div>
                            <div className="text-3xl font-bold text-green-600">
                                {stats.retention_days} dias
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-sm text-gray-600 mb-2">Status</div>
                            <div className="text-lg font-bold text-green-600">
                                {schedulerStatus?.is_running ? '✅ Ativo' : '❌ Inativo'}
                            </div>
                        </div>
                    </div>
                )}

                {/* Scheduler Info */}
                {schedulerStatus && schedulerStatus.is_running && (
                    <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-6 rounded-lg">
                        <h3 className="text-lg font-semibold text-green-800 mb-3">
                            📅 Agendamentos Automáticos
                        </h3>
                        <div className="space-y-2">
                            {schedulerStatus.jobs?.map((job, index) => (
                                <div key={index} className="text-sm text-green-700">
                                    <span className="font-medium">{job.name}:</span>{' '}
                                    {job.next_run ? (
                                        <span>Próxima execução em {formatDate(job.next_run)}</span>
                                    ) : (
                                        <span>Agendado</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Ações Rápidas</h2>
                    <div className="flex gap-4">
                        <button
                            onClick={handleCreateBackup}
                            disabled={creatingBackup}
                            className={`flex-1 bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition ${
                                creatingBackup ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            {creatingBackup ? '⏳ Criando backup...' : '➕ Criar Backup Manual'}
                        </button>
                        <button
                            onClick={fetchBackupData}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                        >
                            🔄 Atualizar
                        </button>
                    </div>
                </div>

                {/* Error Message */}
                {error && !error.includes('Acesso negado') && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
                        <p className="text-red-700">{error}</p>
                    </div>
                )}

                {/* Backups List */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                        📦 Backups Disponíveis ({backups.length})
                    </h2>
                    
                    {backups.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <p className="text-lg mb-2">Nenhum backup encontrado</p>
                            <p className="text-sm">
                                Clique em "Criar Backup Manual" para criar o primeiro backup
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Nome
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Data/Hora
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Tamanho
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Idade
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Ações
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {backups.map((backup, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {backup.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(backup.created_at)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {backup.size_mb} MB
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {backup.age_days === 0 ? 'Hoje' : `${backup.age_days} dia(s)`}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <button
                                                    onClick={() => handleRestoreBackup(backup.name.replace('.tar.gz', ''))}
                                                    disabled={restoring}
                                                    className={`text-red-600 hover:text-red-800 font-medium ${
                                                        restoring ? 'opacity-50 cursor-not-allowed' : ''
                                                    }`}
                                                >
                                                    {restoring ? '⏳ Restaurando...' : '🔄 Restaurar'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Info Box */}
                <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-800 mb-3">ℹ️ Informações Importantes</h3>
                    <ul className="list-disc list-inside space-y-2 text-sm text-blue-700">
                        <li>Backups são criados automaticamente a cada hora</li>
                        <li>Os backups são mantidos por {stats?.retention_days || 30} dias</li>
                        <li>Backups incluem: banco de dados MongoDB e arquivos de configuração</li>
                        <li>⚠️ Restaurar um backup sobrescreverá TODOS os dados atuais</li>
                        <li>Apenas usuários master podem gerenciar backups</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default BackupManagement;
